/**
 * canonical-directory-aggregator.ts
 *
 * Canonical Principal Directory Aggregator & Receipt-Time Presence Clamping.
 * Merges multi-device topology nodes into single human DirectoryUser records.
 */

import { globalWorkspaceDirectory, type WorkspaceMember } from './workspace-directory.js';
import { globalWorkspaceStore, type WorkspaceMember as StoreMember, type MeshTopologyNode } from '../../store/workspace-store.js';

export type PresenceStatus = 'online' | 'away' | 'offline' | 'unknown';

export interface DirectoryDevice {
  readonly deviceId: string;
  readonly deviceName: string;
  readonly lastSeenAt: string;
  readonly isComputeOnly: boolean;
}

export interface DirectoryUser {
  readonly principalId: string;
  readonly displayName: string;
  readonly handle: string; // e.g. "@admin2"
  readonly role: 'admin' | 'member' | 'guest' | 'compute-only';
  readonly presence: PresenceStatus;
  readonly lastSeenAt: string;
  readonly devices: readonly DirectoryDevice[];
  readonly isSelf: boolean;
  readonly assignmentEligible: boolean;
}

export class CanonicalDirectoryAggregator {
  private readonly maxClockSkewMs = 60_000; // 60s max skew

  /**
   * Clamps claimed presence timestamp at receipt time: Math.min(claimed, receivedAt + 60s).
   */
  clampPresenceTimestamp(claimedLastSeenAt: string, receivedAtMs: number = Date.now()): string {
    const claimedMs = new Date(claimedLastSeenAt).getTime();
    if (isNaN(claimedMs)) {
      return new Date(receivedAtMs).toISOString();
    }
    const clampedMs = Math.min(claimedMs, receivedAtMs + this.maxClockSkewMs);
    return new Date(clampedMs).toISOString();
  }

  /**
   * Aggregates and merges canonical principals across all directory sources.
   * Deduplicates multiple devices per principal and strictly segregates compute-only nodes from human assignees.
   */
  getAssignableMembers(workspaceId: string = '*'): readonly DirectoryUser[] {
    const usersMap = new Map<string, DirectoryUser>();
    const now = Date.now();

    // 1. Ingest from WorkspaceDirectory
    const wsMembers = globalWorkspaceDirectory.getMembers(workspaceId);
    for (const m of wsMembers) {
      if (m.role === 'compute-only' || !m.canReceiveAssignments) continue;

      const presence: PresenceStatus = m.status === 'active' ? 'online' : 'offline';
      usersMap.set(m.principalId, {
        principalId: m.principalId,
        displayName: m.displayName || m.principalId,
        handle: `@${m.displayName || m.principalId}`,
        role: m.role,
        presence,
        lastSeenAt: new Date(now).toISOString(),
        devices: [],
        isSelf: false,
        assignmentEligible: true,
      });
    }

    // 2. Ingest from WorkspaceStore Members
    const storeMembers = globalWorkspaceStore.getMembers();
    for (const sm of storeMembers) {
      const existing = usersMap.get(sm.id);
      const isOnline = sm.status === 'Online' || sm.status === 'Pending Auth';
      const presence: PresenceStatus = isOnline ? 'online' : 'offline';

      if (existing) {
        usersMap.set(sm.id, {
          ...existing,
          displayName: sm.name || existing.displayName,
          isSelf: sm.isSelf || existing.isSelf,
          presence: isOnline ? 'online' : existing.presence,
        });
      } else {
        usersMap.set(sm.id, {
          principalId: sm.id,
          displayName: sm.name,
          handle: `@${sm.name}`,
          role: sm.role === 'admin' ? 'admin' : 'member',
          presence,
          lastSeenAt: new Date(now).toISOString(),
          devices: [],
          isSelf: !!sm.isSelf,
          assignmentEligible: true,
        });
      }
    }

    // 3. Ingest Topology Nodes as human-assignable device attachments (or fallback users)
    const topologyNodes = globalWorkspaceStore.getTopologyNodes();
    for (const node of topologyNodes) {
      const isComputeOnly = node.servicesExposed.includes('Compute Only') || node.servicesExposed.includes('Helper Node');
      if (isComputeOnly) continue; // Segregate compute-only hardware

      const principalKey = node.hostname.replace(' (Local)', '').trim();
      const existing = usersMap.get(principalKey);
      const isOnline = node.status === 'ONLINE';
      const presence: PresenceStatus = isOnline ? 'online' : 'offline';

      const device: DirectoryDevice = {
        deviceId: node.tailscaleIp,
        deviceName: node.hostname,
        lastSeenAt: new Date(now).toISOString(),
        isComputeOnly: false,
      };

      if (existing) {
        usersMap.set(principalKey, {
          ...existing,
          devices: [...existing.devices, device],
          presence: isOnline ? 'online' : existing.presence,
        });
      } else {
        usersMap.set(principalKey, {
          principalId: principalKey,
          displayName: principalKey,
          handle: `@${principalKey}`,
          role: 'member',
          presence,
          lastSeenAt: new Date(now).toISOString(),
          devices: [device],
          isSelf: node.hostname.includes('(Local)'),
          assignmentEligible: true,
        });
      }
    }

    return Array.from(usersMap.values());
  }

  /**
   * Returns all human principals regardless of assignment eligibility.
   * Alias for getAssignableMembers('*') for use by non-scrum views.
   */
  getAllPrincipals(): readonly DirectoryUser[] {
    return this.getAssignableMembers('*');
  }
}

export const globalCanonicalDirectory = new CanonicalDirectoryAggregator();
